/**
 * useExportClients - Hook d'export CSV pour les clients
 *
 * Pattern: Component → Hook → Service (API)
 * Itère toutes les pages pour exporter tous les clients (pas juste la page courante)
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { clientsAPI } from '@/utils/clientsAPI';
import { downloadCSV, fetchAllPages, formatDateForExport, formatCurrencyForExport, type ExportColumn } from '@/utils/exportUtils';
import { logger } from '@/utils/logger';
import { notify } from '@/stores/notificationStore';
import type { Client } from '@/types/models';

interface ExportClientsFilters {
  search?: string;
  activeOnly?: boolean;
  clientType?: string[];
}

export function useExportClients() {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(async (filters: ExportClientsFilters = {}) => {
    setIsExporting(true);
    try {
      const allClients = await fetchAllPages<Client>(async (page) => {
        const response = await clientsAPI.getAll({
          page,
          limit: 100,
          search: filters.search || undefined,
          activeOnly: filters.activeOnly,
          clientType: filters.clientType,
        });
        return { data: response.clients, totalPages: response.pagination.pages };
      });

      const columns: ExportColumn<Client>[] = [
        { key: 'companyName', header: t('clients.form.companyName') },
        { key: 'fullName', header: t('clients.form.fullName') },
        {
          key: 'type',
          header: t('clients.form.type'),
          formatter: (value) => value ? t(`clients.type.${value}`, { defaultValue: String(value) }) : '',
        },
        { key: 'email', header: t('clients.fields.email') },
        { key: 'phone', header: t('clients.fields.phone') },
        { key: 'sirenSiret', header: t('clients.fields.siret') },
        { key: 'vatNumber', header: t('clients.fields.vatNumber') },
        { key: 'addressLine1', header: t('clients.form.addressLine1') },
        { key: 'city', header: t('clients.fields.city') },
        { key: 'zipCode', header: t('clients.fields.zipCode') },
        { key: 'countryCode', header: t('clients.fields.country') },
        {
          key: 'totalRevenue',
          header: t('clients.export.totalRevenue'),
          formatter: (value) => formatCurrencyForExport(Number(value) || 0),
        },
        {
          key: 'totalInvoicesCount',
          header: t('clients.export.totalInvoices'),
          formatter: (value) => String(Number(value) || 0),
        },
        {
          key: 'isActive',
          header: t('common.labels.status'),
          formatter: (value) => value ? t('common.labels.active') : t('common.labels.inactive'),
        },
        {
          key: 'createdAt',
          header: t('common.labels.createdAt'),
          formatter: (value) => formatDateForExport(value as string),
        },
      ];

      const today = new Date().toISOString().split('T')[0];
      downloadCSV(allClients, columns, {
        filename: `clients_export_${today}`,
        format: 'csv',
      });
    } catch (error) {
      logger.error('Export clients error:', error);
      notify.error(t('common.export.error'), t('clients.errorTitle'));
    } finally {
      setIsExporting(false);
    }
  }, [t]);

  return { exportCSV, isExporting };
}
